import { useState, useContext, useEffect } from "react";
import LicenseTable from "../components/companies/LicenseTable";
import AddLicense from "../components/companies/AddLicense";
import ConfirmationModal from "../components/common/ConfirmationModal";
import { useLocation } from "react-router-dom";
import { Box, Container, Button,Typography } from "@mui/material";
// Removed Mantine Select import to avoid conflicts; using MUI Select instead
import AddIcon from '@mui/icons-material/Add';
import { Select as MuiSelect, MenuItem ,useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faDownload } from "@fortawesome/free-solid-svg-icons";
import AttachDevicesModal from "../components/companies/AttachLicence";
import AuthContext from "../AuthContext";
import { getLicences, addLicence, notLicenced, deleteLicence } from "../apis/axios";
import { toast } from "react-toastify";

function License() {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const userType = auth.userType;
  const routeCompanyId = location.state?.companyId || null;
  const theme = useTheme();
  // Wait until companyId is ready
  const companyIdReady = userType === "admin" ? auth.admin : routeCompanyId;

  // States
  const [loading, setLoading] = useState(true);
  const [licenses, setLicenses] = useState([]);
  const [devices, setDevices] = useState([]);
  const [tableInstance, setTableInstance] = useState(null);
  const [updateData, setUpdateData] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [type, setType] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    expiry_date: "",
    devices: "",
    no_of_deletions: "",
    company_name: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteAction, setDeleteAction] = useState("");
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);

  const licenceType = [
  { value: "FULL_ACCESS", label: "Full Access (All Features)" },
  { value: "DELETION_ACCESS", label: "Deletion Only" },
  { value: "ENCRYPTION_ACCESS", label: "Encryption / Decryption Only" },
  { value: "DELETION_USB_ACCESS", label: "Deletion + USB Blocking" },
  { value: "ENCRYPTION_USB_ACCESS", label: "Encryption/Decryption + USB Blocking" },
];


  // --- Fetch Devices ---
  const getDevices = async (cid) => {
    try {
      const response = await notLicenced({ company_id: cid });
      setDevices(response.data || []);
    } catch (error) {
      console.error("Error fetching devices:", error);
      setDevices([]);
    }
  };

  // --- Fetch Licenses ---
  const fetchLicenses = async (cid) => {
    try {
      const response = await getLicences({ company_id: cid });
      setLicenses(response.data || []);
    } catch (error) {
      console.error("Error fetching licenses:", error);
      setLicenses([]);
    }
  };

  // --- Initialize on companyIdReady ---
  useEffect(() => {
    if (!companyIdReady) return;

    const init = async () => {
      setLoading(true);
      await getDevices(companyIdReady);
      await fetchLicenses(companyIdReady);
      setLoading(false);
    };
    init();
  }, [companyIdReady, updateData]);

  const handleUpdateData = () => {
    setUpdateData(!updateData);
  };

  // --- Form Handlers ---
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddLicense = async () => {
    const payload={
        expiry: formData.expiry_date,
        type,
        no_of_devices:formData.devices,
        company_id: parseInt(companyIdReady),
      }
      //console.log(payload)
    try {
      const response = await addLicence(payload);
      //console.log(response);
      if (response.data) {
        handleUpdateData();
        toast.success("License added successfully");
        closeModal();
      }
    } catch (error) {
      console.error("Error adding license:", error);
      toast.error("Failed to add license");
    }
  };

  const handleEditLicense = (license) => {
    setFormData(license);
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleDeleteConfirmed = async () => {
    try {
      if (deleteAction === "single") {
        await deleteLicence({ licence_id: deleteTarget });
        handleUpdateData();
        toast.success("License deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting license:", error);
      toast.error("Failed to delete license(s)");
    } finally {
      setConfirmOpen(false);
      setDeleteTarget(null);
      setDeleteAction("");
    }
  };

  const confirmDeleteLicense = (licenseId) => {
    setDeleteTarget(licenseId);
    setDeleteAction("single");
    setConfirmOpen(true);
  };

  const confirmBulkDelete = (ids) => {
    setDeleteTarget(ids);
    setDeleteAction("bulk");
    setConfirmOpen(true);
  };

  const openAttachModal = (license) => {
    setSelectedLicense(license);
    setAttachModalOpen(true);
  };

  const handleDevicesAttached = (licenseId, devicesArr) => {
    handleUpdateData();
  };

  const handleBulkAction = (action) => {
    if (!tableInstance) return;
    if (action === "deleteAll") {
      confirmBulkDelete(licenses.map((lic) => lic.id));
    } else if (action === "deleteSelected") {
      const selectedRows = tableInstance.getSelectedRowModel().rows;
      const selectedIds = selectedRows.map((row) => row.original.id);
      confirmBulkDelete(selectedIds);
    }
  };

  const closeModal = () => {
    setOpenModal(false);
    setIsEditing(false);
    setFormData({
      id: null,
      expiry_date: "",
      devices: "",
      no_of_deletions: "",
      company_name: "",
    });
  };

  const handleRippleEffect = (e) => {
    const button = e.currentTarget;
    const ripple = document.createElement("span");
    const diameter = Math.max(button.clientWidth, button.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
    ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
    ripple.classList.add("export-button-ripple");

    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
  };

  const exportToCSV = (data, filename) => {
    const headers = ["Company", "No. of Devices", "No. of Deletions", "Expiry"];
    const rows = data.map((item) => [
      item.company_name,
      item.no_of_devices,
      item.no_of_deletions,
      item.expiry_date,
    ]);
    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div>Loading licenses...</div>;

  return (
    <Box display="flex" sx={{ maxWidth: "none", justifyContent: "center" }} >
      <Box flexGrow={1} ml={{ sm: 0 }} sx={{ maxWidth: "none" }}>
        <Container
          className="py-4"
          sx={{ maxWidth: "none !important", overflowX: "hidden", paddingLeft: 0, paddingRight: 0 }}
        >
          <div style={{ margin: "0 auto", width: "75.5vw", maxWidth: "75.5vw" }}>
            {/* Header and Buttons */}
            <div className="flex justify-between items-center mb-4 flex-col md:flex-row">
              <Typography
              variant="h4"
                style={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontSize: "2.39vw",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                Licenses
              </Typography>
             {auth.userType==='superadmin' &&(
                <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={(e) => {
                                  handleRippleEffect(e);
                                  setOpenModal(true);
                                  setIsEditing(false);
                                }}
                                sx={{
                                  borderRadius: '20px',
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  bgcolor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                  '&:hover': {
                                    bgcolor: theme.palette.primary.dark,
                                  },
                                }}
                              >
                                Add Licence
                              </Button>
             )}
              {/* Modals */}
          <AddLicense
            open={openModal}
            onClose={closeModal}
            formData={formData}
            companyId={companyIdReady}
            handleInputChange={handleInputChange}
            handleAddLicense={handleAddLicense}
            handleRippleEffect={handleRippleEffect}
            isEditing={isEditing}
            userType={userType}
            type={type}
            setType={setType}
            licenceType={licenceType} // Ensured to pass the defined array
          />
            </div>

            {/* License Table */}
            {licenses.length > 0 ? (
              <LicenseTable
                data={licenses}
                setLicenses={setLicenses}
                onEdit={handleEditLicense}
                onAttachDevices={openAttachModal}
                onDelete={confirmDeleteLicense}
                setTableInstance={setTableInstance}
              />
            ) : (
              <div>No licenses available</div>
            )}
          </div>
          
          <AttachDevicesModal
            open={attachModalOpen}
            onClose={() => {
              setAttachModalOpen(false);
              setSelectedLicense(null);
            }}
            devices={devices}
            selectedLicense={selectedLicense}
            onAttached={handleDevicesAttached}
          />
        </Container>
      </Box>

      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirmed}
        title="Confirm Deletion"
        message="Are you sure you want to delete the defined license(s)?"
      />
    </Box>
  );
}

export default License;